// ---------------------------------------------------------------------------
// Wave 3: 31 Puck Editor Components
// ---------------------------------------------------------------------------
// All use CSS variables (NO hardcoded colors). Responsive. Inline styles only.
// No external dependencies in rendered output.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from "react";
import { CONTENT_WIDTH_OPTION_VALUES, CONTENT_WIDTHS, FONT_SIZE_OPTION_VALUES, SPACING_OPTION_VALUES, fontSize, spacing } from "@ease/page-builder";
import {
  createImageField,
  createStructuredArrayField,
} from "@/components/pages/puck-authoring-fields";
import { BeforeAfterSlider } from "@/components/pages/before-after-slider";

function sanitizeHref(url: string): string {
  const trimmed = url.trim().toLowerCase();
  if (trimmed.startsWith("javascript:") || trimmed.startsWith("data:") || trimmed.startsWith("vbscript:")) {
    return "#";
  }
  return url;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: `${SPACING_OPTION_VALUES.xs}px ${SPACING_OPTION_VALUES.sm}px`,
  border: "1px solid var(--color-border)",
  borderRadius: "8px",
  boxSizing: "border-box",
  background: "var(--color-background)",
  color: "var(--color-foreground)",
  fontSize: FONT_SIZE_OPTION_VALUES.sm,
};

const btnPrimary: React.CSSProperties = {
  padding: `${SPACING_OPTION_VALUES.xs}px ${SPACING_OPTION_VALUES.md}px`,
  background: "var(--color-primary)",
  color: "white",
  border: "none",
  borderRadius: "8px",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: FONT_SIZE_OPTION_VALUES.sm,
};

function normalizeImageList(
  value: unknown,
): Array<{ src: string; alt?: string }> {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      const src = typeof item.src === "string" ? item.src : "";
      const alt = typeof item.alt === "string" ? item.alt : "";
      return src || alt ? [{ src, alt }] : [];
    });
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((src, index) => ({ src, alt: `Image ${index + 1}` }));
  }

  return [];
}

function normalizeSocialProofNotifications(
  value: unknown,
): Array<{ name: string; location: string; action: string; timeAgo: string }> {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (!item || typeof item !== "object") return [];
      const record = item as Record<string, unknown>;
      const next = {
        name: typeof record.name === "string" ? record.name : "",
        location: typeof record.location === "string" ? record.location : "",
        action: typeof record.action === "string" ? record.action : "",
        timeAgo: typeof record.timeAgo === "string" ? record.timeAgo : "",
      };
      return next.name || next.location || next.action || next.timeAgo ? [next] : [];
    });
  }

  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [name, location, action, timeAgo] = line.split("|").map((part) => part.trim());
        return {
          name: name ?? "",
          location: location ?? "",
          action: action ?? "",
          timeAgo: timeAgo ?? "",
        };
      });
  }

  return [];
}

// ---------------------------------------------------------------------------
// 1. Container
// ---------------------------------------------------------------------------
export const Container = {
  fields: {
    maxWidth: { type: "select", options: [{ label: "Small (640px)", value: "640" }, { label: "Medium (800px)", value: "800" }, { label: "Large (1024px)", value: "1024" }, { label: "Full", value: "100%" }] },
    padding: { type: "select", options: [{ label: "None", value: "0" }, { label: "Small", value: "16" }, { label: "Medium", value: "32" }, { label: "Large", value: "64" }] },
    background: { type: "select", options: [{ label: "Transparent", value: "transparent" }, { label: "Card", value: "var(--color-card)" }, { label: "Muted", value: "var(--color-muted)" }] },
  },
  defaultProps: { maxWidth: CONTENT_WIDTH_OPTION_VALUES.md, padding: SPACING_OPTION_VALUES.md, background: "transparent" },
  render: ({ maxWidth, padding, background }: { maxWidth: string; padding: string; background: string }) => (
    <div style={{ maxWidth: maxWidth === "100%" ? "100%" : `${maxWidth}px`, margin: "0 auto", padding: `${padding}px`, background }}>
      <div style={{ minHeight: "60px", border: "1px dashed var(--color-border)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-muted-foreground)", fontSize: "0.875rem" }}>
        Container — drop components here
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 2. Grid
// ---------------------------------------------------------------------------
export const Grid = {
  fields: {
    columns: { type: "select", options: [{ label: "2 Columns", value: "2" }, { label: "3 Columns", value: "3" }, { label: "4 Columns", value: "4" }] },
    gap: { type: "select", options: [{ label: "Small (8px)", value: "8" }, { label: "Medium (16px)", value: "16" }, { label: "Large (24px)", value: "24" }] },
  },
  defaultProps: { columns: "3", gap: SPACING_OPTION_VALUES.sm },
  render: ({ columns, gap }: { columns: string; gap: string }) => (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: `${gap}px`, padding: spacing(3) }}>
      {Array.from({ length: parseInt(columns) }, (_, i) => (
        <div key={i} style={{ minHeight: "80px", background: "var(--color-muted)", borderRadius: "8px", border: "1px dashed var(--color-border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-muted-foreground)", fontSize: "0.875rem" }}>
          Cell {i + 1}
        </div>
      ))}
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 3. Tabs
// ---------------------------------------------------------------------------
export const Tabs = {
  fields: { items: { type: "textarea", label: "Tabs (Title | Content per line)" } },
  defaultProps: { items: "Overview | Our platform helps businesses automate their entire workflow from lead capture to payment.\nFeatures | AI-powered CRM, automated email sequences, landing pages, and appointment booking.\nPricing | Plans start at \u20AC29/mo for small teams up to \u20AC249/mo for enterprise." },
  render: ({ items }: { items: string }) => {
    const tabs = items.split("\n").filter(Boolean).map((line) => {
      const [title, content] = line.split("|").map((s) => s.trim());
      return { title: title ?? "", content: content ?? "" };
    });
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.lg, margin: "0 auto", padding: `${SPACING_OPTION_VALUES.sm}px` }}>
        <div style={{ display: "flex", borderBottom: "2px solid var(--color-border)" }}>
          {tabs.map((tab, i) => (
            <div key={i} style={{ padding: "10px 20px", fontWeight: 600, fontSize: "0.875rem", color: i === 0 ? "var(--color-primary)" : "var(--color-muted-foreground)", borderBottom: i === 0 ? "2px solid var(--color-primary)" : "none", marginBottom: "-2px", cursor: "pointer" }}>
              {tab.title}
            </div>
          ))}
        </div>
        {tabs[0] && <div style={{ padding: "20px 0", color: "var(--color-foreground)", lineHeight: 1.6 }}>{tabs[0].content}</div>}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 4. StickySection
// ---------------------------------------------------------------------------
export const StickySection = {
  fields: {
    content: { type: "textarea" },
    background: { type: "select", options: [{ label: "Card", value: "var(--color-card)" }, { label: "Primary", value: "var(--color-primary)" }, { label: "Muted", value: "var(--color-muted)" }] },
  },
  defaultProps: { content: "Limited time offer — 30% off all annual plans. Use code LAUNCH30.", background: "var(--color-primary)" },
  render: ({ content, background }: { content: string; background: string }) => (
    <div style={{ position: "sticky", top: 0, zIndex: 10, background, padding: "12px 24px", textAlign: "center", color: background.includes("primary") ? "white" : "var(--color-foreground)", fontSize: "0.875rem", fontWeight: 600 }}>
      {content}
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 5. Carousel
// ---------------------------------------------------------------------------
export const Carousel = {
  fields: {
    images: createStructuredArrayField({
      label: "Carousel images",
      itemLabel: "Slide",
      addItemLabel: "Add slide",
      itemFields: [
        { key: "src", label: "Image", type: "image", placeholder: "Choose or upload a slide image" },
        { key: "alt", label: "Alt text", type: "text", placeholder: "Describe the slide for accessibility" },
      ],
      getDefaultItem: () => ({ src: "", alt: "" }),
      normalizeValue: normalizeImageList,
    }),
    height: { type: "number" },
  },
  defaultProps: { images: [], height: 400 },
  render: ({ images, height }: { images: unknown; height: number }) => {
    const slides = normalizeImageList(images);
    return (
      <div style={{ position: "relative", overflow: "hidden", borderRadius: "12px", margin: "0 auto", maxWidth: CONTENT_WIDTHS.xl }}>
        <div style={{ display: "flex", overflowX: "auto", scrollSnapType: "x mandatory", gap: "0" }}>
          {slides.length > 0 ? slides.map((slide, i) => (
            <div key={i} style={{ minWidth: "100%", height: `${height}px`, scrollSnapAlign: "start", flexShrink: 0 }}>
              <img src={slide.src} alt={slide.alt || `Slide ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )) : (
            Array.from({ length: 3 }, (_, i) => (
              <div key={i} style={{ minWidth: "100%", height: `${height}px`, scrollSnapAlign: "start", flexShrink: 0, background: "var(--color-muted)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-muted-foreground)" }}>
                Slide {i + 1}
              </div>
            ))
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "8px", padding: "12px" }}>
          {Array.from({ length: Math.max(slides.length, 3) }, (_, i) => (
            <div key={i} style={{ width: "8px", height: "8px", borderRadius: "50%", background: i === 0 ? "var(--color-primary)" : "var(--color-border)" }} />
          ))}
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 6. Timeline
// ---------------------------------------------------------------------------
export const Timeline = {
  fields: { events: { type: "textarea", label: "Events (Date | Title | Description per line)" } },
  defaultProps: { events: "2024 | Company Founded | Started with a vision to simplify business operations.\n2025 | 1,000 Customers | Reached our first major milestone across 12 countries.\n2026 | AI Launch | Introduced AI-powered automation to every plan." },
  render: ({ events }: { events: string }) => {
    const items = events.split("\n").filter(Boolean).map((line) => {
      const [date, title, desc] = line.split("|").map((s) => s.trim());
      return { date: date ?? "", title: title ?? "", description: desc ?? "" };
    });
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto", padding: `${SPACING_OPTION_VALUES.xl}px ${SPACING_OPTION_VALUES.sm}px`, position: "relative" }}>
        <div style={{ position: "absolute", left: "50%", top: "40px", bottom: "40px", width: "2px", background: "var(--color-border)", transform: "translateX(-50%)" }} />
        {items.map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", marginBottom: "32px", flexDirection: i % 2 === 0 ? "row" : "row-reverse", textAlign: i % 2 === 0 ? "right" : "left" }}>
            <div style={{ flex: 1, padding: "0 24px" }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--color-primary)" }}>{item.date}</div>
              <div style={{ fontWeight: 600, color: "var(--color-foreground)", marginTop: "4px" }}>{item.title}</div>
              <div style={{ fontSize: "0.85rem", color: "var(--color-muted-foreground)", marginTop: "4px", lineHeight: 1.5 }}>{item.description}</div>
            </div>
            <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "var(--color-primary)", border: "3px solid var(--color-background)", flexShrink: 0, zIndex: 1 }} />
            <div style={{ flex: 1 }} />
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 7. Blockquote
// ---------------------------------------------------------------------------
export const Blockquote = {
  fields: { quote: { type: "textarea" }, author: { type: "text" }, role: { type: "text" } },
  defaultProps: { quote: "The best way to predict the future is to create it.", author: "Peter Drucker", role: "Management Consultant" },
  render: ({ quote, author, role }: { quote: string; author: string; role: string }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto", padding: `${SPACING_OPTION_VALUES.xl}px ${SPACING_OPTION_VALUES.sm}px` }}>
      <blockquote style={{ borderLeft: "4px solid var(--color-primary)", paddingLeft: "20px", margin: 0 }}>
        <p style={{ fontSize: fontSize("lg"), fontStyle: "italic", color: "var(--color-foreground)", lineHeight: 1.6 }}>"{quote}"</p>
        <footer style={{ marginTop: "12px" }}>
          <span style={{ fontWeight: 600, color: "var(--color-foreground)" }}>{author}</span>
          {role && <span style={{ color: "var(--color-muted-foreground)", marginLeft: "8px" }}>{role}</span>}
        </footer>
      </blockquote>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 8. CodeBlock
// ---------------------------------------------------------------------------
export const CodeBlock = {
  fields: { code: { type: "textarea" }, language: { type: "text" } },
  defaultProps: { code: "const response = await fetch('/api/leads');\nconst leads = await response.json();\nconsole.log(`Found ${leads.length} leads`);", language: "JavaScript" },
  render: ({ code, language }: { code: string; language: string }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.md, margin: "0 auto", padding: spacing(3) }}>
      <div style={{ background: "#1e1e2e", borderRadius: "8px", overflow: "hidden" }}>
        <div style={{ padding: "8px 16px", background: "#2a2a3e", fontSize: "0.75rem", color: "#a0a0b8" }}>{language}</div>
        <pre style={{ padding: "16px", margin: 0, overflowX: "auto", color: "#e0e0f0", fontSize: "0.85rem", lineHeight: 1.6, fontFamily: "monospace" }}>
          <code>{code}</code>
        </pre>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 9. Alert
// ---------------------------------------------------------------------------
export const Alert = {
  fields: {
    message: { type: "textarea" },
    variant: { type: "select", options: [{ label: "Info", value: "info" }, { label: "Warning", value: "warning" }, { label: "Success", value: "success" }, { label: "Error", value: "error" }] },
  },
  defaultProps: { message: "Your free trial expires in 3 days. Upgrade now to keep all your data.", variant: "info" },
  render: ({ message, variant }: { message: string; variant: string }) => {
    const config: Record<string, { bg: string; border: string; icon: string }> = {
      info: { bg: "var(--color-primary)", border: "var(--color-primary)", icon: "\u2139" },
      warning: { bg: "var(--color-warning)", border: "var(--color-warning)", icon: "\u26A0" },
      success: { bg: "var(--color-success)", border: "var(--color-success)", icon: "\u2713" },
      error: { bg: "var(--color-destructive)", border: "var(--color-destructive)", icon: "\u2717" },
    };
    const c = config[variant] ?? config.info!;
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.md, margin: "0 auto", padding: spacing(3) }}>
        <div style={{ display: "flex", gap: "12px", padding: "14px 18px", borderRadius: "8px", borderLeft: `4px solid ${c.border}`, background: "var(--color-card)" }}>
          <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>{c.icon}</span>
          <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--color-foreground)", lineHeight: 1.5 }}>{message}</p>
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 10. DataTable
// ---------------------------------------------------------------------------
export const DataTable = {
  fields: { headers: { type: "text", label: "Headers (comma separated)" }, rows: { type: "textarea", label: "Rows (comma separated, one row per line)" } },
  defaultProps: { headers: "Plan, Price, Contacts, Users", rows: "Starter, \u20AC29/mo, 500, 1\nGrowth, \u20AC89/mo, 5000, 5\nScale, \u20AC249/mo, Unlimited, Unlimited" },
  render: ({ headers, rows }: { headers: string; rows: string }) => {
    const cols = headers.split(",").map((h) => h.trim());
    const dataRows = rows.split("\n").filter(Boolean).map((r) => r.split(",").map((c) => c.trim()));
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.lg, margin: "0 auto", padding: spacing(3), overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{cols.map((h, i) => <th key={i} style={{ padding: "10px 16px", textAlign: "left", borderBottom: "2px solid var(--color-border)", fontSize: "0.85rem", fontWeight: 600, color: "var(--color-foreground)" }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {dataRows.map((row, i) => (
              <tr key={i}>{row.map((cell, j) => <td key={j} style={{ padding: "10px 16px", borderBottom: "1px solid var(--color-border)", fontSize: "0.875rem", color: "var(--color-muted-foreground)" }}>{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 11. VideoBackground
// ---------------------------------------------------------------------------
export const VideoBackground = {
  fields: { videoUrl: { type: "text", label: "Video URL (MP4)" }, heading: { type: "text" }, subtitle: { type: "textarea" }, overlayOpacity: { type: "number" } },
  defaultProps: { videoUrl: "", heading: "Transform Your Business", subtitle: "Join thousands of companies using our AI-powered platform.", overlayOpacity: 60 },
  render: ({ videoUrl, heading, subtitle, overlayOpacity }: { videoUrl: string; heading: string; subtitle: string; overlayOpacity: number }) => (
    <div style={{ position: "relative", minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: "12px" }}>
      {videoUrl ? (
        <video src={videoUrl} autoPlay muted loop playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, var(--color-primary), var(--color-muted))" }} />
      )}
      <div style={{ position: "absolute", inset: 0, background: `rgba(0,0,0,${overlayOpacity / 100})` }} />
      <div style={{ position: "relative", zIndex: 1, textAlign: "center", color: "white", padding: `${spacing(5)} ${spacing(3)}` }}>
        <h2 style={{ fontSize: "2.5rem", fontWeight: 700, marginBottom: "12px" }}>{heading}</h2>
        <p style={{ fontSize: "1.1rem", opacity: 0.9, maxWidth: "500px", margin: "0 auto" }}>{subtitle}</p>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 12. BeforeAfter
// ---------------------------------------------------------------------------
export const BeforeAfter = {
  fields: {
    beforeImage: createImageField({ label: "Before image" }),
    afterImage: createImageField({ label: "After image" }),
    beforeLabel: { type: "text" },
    afterLabel: { type: "text" },
    initialPosition: { type: "number", label: "Initial reveal (%)" },
  },
  defaultProps: { beforeImage: "", afterImage: "", beforeLabel: "Before", afterLabel: "After", initialPosition: 50 },
  render: ({
    beforeImage,
    afterImage,
    beforeLabel,
    afterLabel,
    initialPosition,
  }: {
    beforeImage: string;
    afterImage: string;
    beforeLabel: string;
    afterLabel: string;
    initialPosition: number;
  }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.md, margin: "0 auto", padding: spacing(3) }}>
      <BeforeAfterSlider
        beforeImage={beforeImage}
        afterImage={afterImage}
        beforeLabel={beforeLabel}
        afterLabel={afterLabel}
        initialPosition={initialPosition}
      />
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 13. ImageHotspot
// ---------------------------------------------------------------------------
export const ImageHotspot = {
  fields: { imageUrl: createImageField({ label: "Base image" }), hotspots: { type: "textarea", label: "Hotspots (X% | Y% | Label per line)" } },
  defaultProps: { imageUrl: "", hotspots: "25 | 30 | Reception Desk\n70 | 50 | Treatment Room\n50 | 80 | Waiting Area" },
  render: ({ imageUrl, hotspots }: { imageUrl: string; hotspots: string }) => {
    const spots = hotspots.split("\n").filter(Boolean).map((line) => {
      const [x, y, label] = line.split("|").map((s) => s.trim());
      return { x: x ?? "50", y: y ?? "50", label: label ?? "" };
    });
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.md, margin: "0 auto", padding: spacing(3) }}>
        <div style={{ position: "relative", aspectRatio: "16/9", background: imageUrl ? `url(${imageUrl}) center/cover no-repeat` : "var(--color-muted)", borderRadius: "12px", overflow: "hidden" }}>
          {spots.map((spot, i) => (
            <div key={i} style={{ position: "absolute", left: `${spot.x}%`, top: `${spot.y}%`, transform: "translate(-50%, -50%)" }}>
              <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "var(--color-primary)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>{i + 1}</div>
              <div style={{ position: "absolute", top: "28px", left: "50%", transform: "translateX(-50%)", background: "var(--color-card)", padding: "4px 8px", borderRadius: "4px", fontSize: "0.7rem", whiteSpace: "nowrap", boxShadow: "0 2px 4px rgba(0,0,0,0.15)", color: "var(--color-foreground)" }}>{spot.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 14. ClientLogos
// ---------------------------------------------------------------------------
export const ClientLogos = {
  fields: {
    logos: createStructuredArrayField({
      label: "Client logos",
      itemLabel: "Logo",
      addItemLabel: "Add logo",
      itemFields: [
        { key: "src", label: "Logo", type: "image", placeholder: "Choose or upload a logo" },
        { key: "alt", label: "Alt text", type: "text", placeholder: "Company name" },
      ],
      getDefaultItem: () => ({ src: "", alt: "" }),
      normalizeValue: normalizeImageList,
    }),
    heading: { type: "text" },
  },
  defaultProps: { logos: [], heading: "Trusted by 500+ businesses" },
  render: ({ logos, heading }: { logos: unknown; heading: string }) => {
    const items = normalizeImageList(logos);
    return (
      <div style={{ textAlign: "center", padding: `${spacing(5)} ${spacing(3)}` }}>
      <p style={{ fontSize: "0.85rem", color: "var(--color-muted-foreground)", marginBottom: "24px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>{heading}</p>
      <div style={{ display: "flex", justifyContent: "center", gap: "32px", flexWrap: "wrap", alignItems: "center" }}>
        {items.length > 0 ? items.map((logo, i) => (
          <div key={i} style={{ width: "100px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {logo.src ? (
              <img src={logo.src} alt={logo.alt || `Logo ${i + 1}`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", opacity: 0.8 }} />
            ) : null}
          </div>
        )) : Array.from({ length: 5 }, (_, i) => (
          <div key={i} style={{ width: "100px", height: "40px", background: "var(--color-muted)", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-muted-foreground)", fontSize: "0.7rem", opacity: 0.6 }}>
            Logo {i + 1}
          </div>
        ))}
      </div>
    </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 15. CaseStudy
// ---------------------------------------------------------------------------
export const CaseStudy = {
  fields: { company: { type: "text" }, challenge: { type: "textarea" }, solution: { type: "textarea" }, result: { type: "textarea" }, metric: { type: "text" } },
  defaultProps: { company: "Bright Smiles Dental", challenge: "Losing 40% of leads due to slow follow-up times and manual scheduling.", solution: "Automated lead capture with AI-powered instant responses and smart booking.", result: "Response time dropped from 4 hours to 30 seconds. Bookings increased 85%.", metric: "+85% Bookings" },
  render: ({ company, challenge, solution, result, metric }: { company: string; challenge: string; solution: string; result: string; metric: string }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.md, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}` }}>
      <div style={{ background: "var(--color-card)", borderRadius: "12px", padding: "32px", border: "1px solid var(--color-border)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "24px" }}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--color-foreground)" }}>{company}</h3>
          <span style={{ background: "var(--color-primary)", color: "white", padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 700 }}>{metric}</span>
        </div>
        {[{ label: "Challenge", text: challenge }, { label: "Solution", text: solution }, { label: "Result", text: result }].map((section, i) => (
          <div key={i} style={{ marginBottom: i < 2 ? "16px" : 0 }}>
            <h4 style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>{section.label}</h4>
            <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", lineHeight: 1.6, margin: 0 }}>{section.text}</p>
          </div>
        ))}
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 16. Awards
// ---------------------------------------------------------------------------
export const Awards = {
  fields: { awards: { type: "textarea", label: "Awards (Name | Year per line)" } },
  defaultProps: { awards: "Best SaaS Platform | 2026\nTop Rated on G2 | 2025\nISO 27001 Certified | 2025\nGDPR Compliant | 2024" },
  render: ({ awards }: { awards: string }) => {
    const items = awards.split("\n").filter(Boolean).map((line) => {
      const [name, year] = line.split("|").map((s) => s.trim());
      return { name: name ?? "", year: year ?? "" };
    });
    return (
      <div style={{ display: "flex", justifyContent: "center", gap: spacing(3), flexWrap: "wrap", padding: `${spacing(5)} ${spacing(3)}` }}>
        {items.map((item, i) => (
          <div key={i} style={{ textAlign: "center", padding: "20px", border: "1px solid var(--color-border)", borderRadius: "12px", background: "var(--color-card)", minWidth: "140px" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "var(--color-primary)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: "1.2rem" }}>{"\u2605"}</div>
            <p style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--color-foreground)", margin: 0 }}>{item.name}</p>
            <p style={{ fontSize: "0.75rem", color: "var(--color-muted-foreground)", margin: "4px 0 0" }}>{item.year}</p>
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 17. SocialFeed
// ---------------------------------------------------------------------------
export const SocialFeed = {
  fields: { posts: { type: "textarea", label: "Posts (Platform | Author | Content per line)" }, columns: { type: "select", options: [{ label: "2", value: "2" }, { label: "3", value: "3" }] } },
  defaultProps: { posts: "Twitter | @sarahk | Just switched to this platform and my bookings are up 60% in one month!\nInstagram | @fitnessguru | The AI email sequences are a game-changer for my studio.\nFacebook | Mike R. | Finally a CRM that actually understands service businesses.", columns: "3" },
  render: ({ posts, columns }: { posts: string; columns: string }) => {
    const items = posts.split("\n").filter(Boolean).map((line) => {
      const [platform, author, content] = line.split("|").map((s) => s.trim());
      return { platform: platform ?? "", author: author ?? "", content: content ?? "" };
    });
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: spacing(2), padding: spacing(3), maxWidth: CONTENT_WIDTHS.xl, margin: "0 auto" }}>
        {items.map((item, i) => (
          <div key={i} style={{ padding: "20px", background: "var(--color-card)", borderRadius: "12px", border: "1px solid var(--color-border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
              <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--color-foreground)" }}>{item.author}</span>
              <span style={{ fontSize: "0.75rem", color: "var(--color-muted-foreground)" }}>{item.platform}</span>
            </div>
            <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", lineHeight: 1.5, margin: 0 }}>{item.content}</p>
          </div>
        ))}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 18. Counter
// ---------------------------------------------------------------------------
export const Counter = {
  fields: { value: { type: "text" }, label: { type: "text" }, prefix: { type: "text" }, suffix: { type: "text" } },
  defaultProps: { value: "2,847", label: "Happy Customers", prefix: "", suffix: "+" },
  render: ({ value, label, prefix, suffix }: { value: string; label: string; prefix: string; suffix: string }) => (
    <div style={{ textAlign: "center", padding: `${spacing(6)} ${spacing(3)}` }}>
      <div style={{ fontSize: "4rem", fontWeight: 800, color: "var(--color-primary)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
        {prefix}{value}{suffix}
      </div>
      <p style={{ fontSize: "1rem", color: "var(--color-muted-foreground)", marginTop: "8px" }}>{label}</p>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 19. ROI Calculator
// ---------------------------------------------------------------------------
export function computeRoiValue(
  primary: number,
  secondary: number,
  formula: string,
  multiplier: number,
): number {
  if (formula === "percentage") {
    return primary * (secondary / 100) * multiplier;
  }

  if (formula === "compound") {
    return primary * Math.pow(1 + secondary / 100, multiplier);
  }

  return primary * secondary * multiplier;
}

function formatRoiNumber(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: value >= 1000 ? 0 : 1,
  }).format(value);
}

function RoiCalculatorPreview({
  heading,
  description,
  primaryLabel,
  primaryMin,
  primaryMax,
  primaryDefault,
  primaryStep,
  primaryPrefix,
  primarySuffix,
  secondaryLabel,
  secondaryMin,
  secondaryMax,
  secondaryDefault,
  secondaryStep,
  secondaryPrefix,
  secondarySuffix,
  formula,
  multiplier,
  resultLabel,
  resultPrefix,
  resultSuffix,
  ctaText,
  ctaUrl,
}: {
  heading: string;
  description: string;
  primaryLabel: string;
  primaryMin: number;
  primaryMax: number;
  primaryDefault: number;
  primaryStep: number;
  primaryPrefix: string;
  primarySuffix: string;
  secondaryLabel: string;
  secondaryMin: number;
  secondaryMax: number;
  secondaryDefault: number;
  secondaryStep: number;
  secondaryPrefix: string;
  secondarySuffix: string;
  formula: string;
  multiplier: number;
  resultLabel: string;
  resultPrefix: string;
  resultSuffix: string;
  ctaText?: string;
  ctaUrl?: string;
}) {
  const [primaryValue, setPrimaryValue] = useState(primaryDefault);
  const [secondaryValue, setSecondaryValue] = useState(secondaryDefault);
  const result = useMemo(
    () => computeRoiValue(primaryValue, secondaryValue, formula, multiplier),
    [formula, multiplier, primaryValue, secondaryValue],
  );

  return (
    <section style={{ maxWidth: CONTENT_WIDTHS.md, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}` }}>
      <div style={{ border: "1px solid var(--color-border)", borderRadius: "24px", background: "var(--color-card)", padding: spacing(4), boxShadow: "var(--shadow-md)" }}>
        <div style={{ textAlign: "center", marginBottom: spacing(3) }}>
          <h2 style={{ fontSize: fontSize("2xl"), fontWeight: 700, color: "var(--color-foreground)", marginBottom: spacing(1) }}>{heading}</h2>
          <p style={{ color: "var(--color-muted-foreground)", lineHeight: 1.6 }}>{description}</p>
        </div>

        <div style={{ display: "grid", gap: spacing(3) }}>
          <label style={{ display: "grid", gap: spacing(1) }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: spacing(2), fontSize: FONT_SIZE_OPTION_VALUES.sm }}>
              <span style={{ color: "var(--color-foreground)", fontWeight: 600 }}>{primaryLabel}</span>
              <span style={{ color: "var(--color-primary)", fontWeight: 700 }}>
                {primaryPrefix}{formatRoiNumber(primaryValue)}{primarySuffix}
              </span>
            </div>
            <input
              type="range"
              min={primaryMin}
              max={primaryMax}
              step={primaryStep}
              value={primaryValue}
              onChange={(event) => setPrimaryValue(Number(event.target.value))}
            />
          </label>

          <label style={{ display: "grid", gap: spacing(1) }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: spacing(2), fontSize: FONT_SIZE_OPTION_VALUES.sm }}>
              <span style={{ color: "var(--color-foreground)", fontWeight: 600 }}>{secondaryLabel}</span>
              <span style={{ color: "var(--color-primary)", fontWeight: 700 }}>
                {secondaryPrefix}{formatRoiNumber(secondaryValue)}{secondarySuffix}
              </span>
            </div>
            <input
              type="range"
              min={secondaryMin}
              max={secondaryMax}
              step={secondaryStep}
              value={secondaryValue}
              onChange={(event) => setSecondaryValue(Number(event.target.value))}
            />
          </label>
        </div>

        <div style={{ marginTop: spacing(4), borderRadius: "18px", background: "var(--color-surface)", padding: spacing(3), textAlign: "center" }}>
          <div style={{ fontSize: FONT_SIZE_OPTION_VALUES.sm, color: "var(--color-muted-foreground)", marginBottom: spacing(1) }}>
            {resultLabel}
          </div>
          <div style={{ fontSize: fontSize("3xl"), fontWeight: 800, color: "var(--color-primary)" }}>
            {resultPrefix}{formatRoiNumber(result)}{resultSuffix}
          </div>
          <div style={{ marginTop: spacing(1), fontSize: FONT_SIZE_OPTION_VALUES.sm, color: "var(--color-muted-foreground)" }}>
            {formula === "multiply"
              ? `Calculated as ${primaryLabel.toLowerCase()} × ${secondaryLabel.toLowerCase()} × ${multiplier}.`
              : formula === "percentage"
                ? `Calculated as ${primaryLabel.toLowerCase()} × ${secondaryLabel.toLowerCase()}% × ${multiplier}.`
                : `Calculated with ${multiplier} compounding period${multiplier === 1 ? "" : "s"}.`}
          </div>
        </div>

        {ctaText ? (
          <div style={{ marginTop: spacing(3), textAlign: "center" }}>
            <a
              href={sanitizeHref(ctaUrl ?? "#form")}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: `${SPACING_OPTION_VALUES.sm}px ${SPACING_OPTION_VALUES.lg}px`, borderRadius: "999px", background: "var(--color-primary)", color: "white", textDecoration: "none", fontWeight: 700 }}
            >
              {ctaText}
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export const RoiCalculator = {
  fields: {
    heading: { type: "text", label: "Heading" },
    description: { type: "textarea", label: "Description" },
    primaryLabel: { type: "text", label: "Primary slider label" },
    primaryMin: { type: "number", label: "Primary min" },
    primaryMax: { type: "number", label: "Primary max" },
    primaryDefault: { type: "number", label: "Primary default" },
    primaryStep: { type: "number", label: "Primary step" },
    primaryPrefix: { type: "text", label: "Primary prefix" },
    primarySuffix: { type: "text", label: "Primary suffix" },
    secondaryLabel: { type: "text", label: "Secondary slider label" },
    secondaryMin: { type: "number", label: "Secondary min" },
    secondaryMax: { type: "number", label: "Secondary max" },
    secondaryDefault: { type: "number", label: "Secondary default" },
    secondaryStep: { type: "number", label: "Secondary step" },
    secondaryPrefix: { type: "text", label: "Secondary prefix" },
    secondarySuffix: { type: "text", label: "Secondary suffix" },
    formula: {
      type: "select",
      label: "Formula",
      options: [
        { label: "Multiply", value: "multiply" },
        { label: "Percentage", value: "percentage" },
        { label: "Compound", value: "compound" },
      ],
    },
    multiplier: { type: "number", label: "Multiplier / periods" },
    resultLabel: { type: "text", label: "Result label" },
    resultPrefix: { type: "text", label: "Result prefix" },
    resultSuffix: { type: "text", label: "Result suffix" },
    ctaText: { type: "text", label: "CTA text" },
    ctaUrl: { type: "text", label: "CTA URL" },
  },
  defaultProps: {
    heading: "Estimate the upside",
    description: "Adjust the inputs to show the projected annual return before someone books the next step.",
    primaryLabel: "Monthly leads",
    primaryMin: 10,
    primaryMax: 500,
    primaryDefault: 120,
    primaryStep: 10,
    primaryPrefix: "",
    primarySuffix: "",
    secondaryLabel: "Average deal value",
    secondaryMin: 100,
    secondaryMax: 5000,
    secondaryDefault: 850,
    secondaryStep: 50,
    secondaryPrefix: "$",
    secondarySuffix: "",
    formula: "multiply",
    multiplier: 12,
    resultLabel: "Projected annual revenue",
    resultPrefix: "$",
    resultSuffix: "",
    ctaText: "Book a strategy call",
    ctaUrl: "#form",
  },
  render: (props: {
    heading: string;
    description: string;
    primaryLabel: string;
    primaryMin: number;
    primaryMax: number;
    primaryDefault: number;
    primaryStep: number;
    primaryPrefix: string;
    primarySuffix: string;
    secondaryLabel: string;
    secondaryMin: number;
    secondaryMax: number;
    secondaryDefault: number;
    secondaryStep: number;
    secondaryPrefix: string;
    secondarySuffix: string;
    formula: string;
    multiplier: number;
    resultLabel: string;
    resultPrefix: string;
    resultSuffix: string;
    ctaText?: string;
    ctaUrl?: string;
  }) => <RoiCalculatorPreview {...props} />,
};

function SocialProofPopupPreview({
  notifications,
  displayInterval,
  displayDuration,
  position,
  showOnMobile,
  maxShown,
  randomize,
}: {
  notifications: Array<{ name: string; location: string; action: string; timeAgo: string }>;
  displayInterval: number;
  displayDuration: number;
  position: string;
  showOnMobile: string;
  maxShown: number;
  randomize: string;
}) {
  const normalized = useMemo(
    () => normalizeSocialProofNotifications(notifications).slice(0, Math.max(1, Math.min(maxShown, 20))),
    [maxShown, notifications],
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (normalized.length <= 1) return;
    const interval = setInterval(() => {
      setIndex((current) => (current + 1) % normalized.length);
    }, Math.max(displayInterval, 3) * 1000);
    return () => clearInterval(interval);
  }, [displayInterval, normalized.length]);

  const ordered = useMemo(() => {
    if (randomize !== "yes" || normalized.length <= 1) return normalized;
    return [...normalized].sort((left, right) => `${left.name}${left.action}`.localeCompare(`${right.name}${right.action}`));
  }, [normalized, randomize]);

  const current = ordered[index] ?? ordered[0];
  if (!current) {
    return (
      <div style={{ maxWidth: "22rem", margin: "0 auto", padding: spacing(3), borderRadius: "18px", border: "1px dashed var(--color-border)", color: "var(--color-muted-foreground)" }}>
        Add at least one social-proof notification to preview the popup.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "22rem", margin: "0 auto", padding: spacing(3) }}>
      <div style={{ borderRadius: "18px", border: "1px solid var(--color-border)", background: "var(--color-card)", boxShadow: "var(--shadow-lg)", padding: spacing(2), display: "grid", gap: spacing(1) }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: spacing(1.5) }}>
          <div aria-hidden="true" style={{ width: "0.75rem", height: "0.75rem", borderRadius: "999px", background: "var(--color-primary)", marginTop: "0.35rem", flexShrink: 0 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ color: "var(--color-foreground)", lineHeight: 1.45, fontSize: FONT_SIZE_OPTION_VALUES.sm }}>
              <strong>{current.name || "Someone"}</strong>{" "}
              <span>{current.action || "took action"}</span>
            </div>
            <div style={{ color: "var(--color-muted-foreground)", fontSize: "0.8rem", marginTop: "0.2rem" }}>
              {[current.location, current.timeAgo].filter(Boolean).join(" • ")}
            </div>
          </div>
          <span style={{ color: "var(--color-muted-foreground)", fontSize: "0.75rem" }}>×</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: spacing(2), fontSize: "0.75rem", color: "var(--color-muted-foreground)" }}>
          <span>{position}</span>
          <span>{showOnMobile === "yes" ? "mobile on" : "mobile off"}</span>
          <span>{displayDuration}s visible</span>
        </div>
      </div>
    </div>
  );
}

export const SocialProofPopup = {
  fields: {
    notifications: createStructuredArrayField({
      label: "Notifications",
      itemLabel: "Notification",
      addItemLabel: "Add notification",
      itemFields: [
        { key: "name", label: "Name", type: "text", placeholder: "Ava Johnson" },
        { key: "location", label: "Location", type: "text", placeholder: "Berlin, Germany" },
        { key: "action", label: "Action", type: "text", placeholder: "booked a strategy call" },
        { key: "timeAgo", label: "Time ago", type: "text", placeholder: "2 minutes ago" },
      ],
      getDefaultItem: () => ({ name: "", location: "", action: "", timeAgo: "" }),
      normalizeValue: normalizeSocialProofNotifications,
      maxItems: 20,
    }),
    displayInterval: { type: "number", label: "Display interval (seconds)" },
    displayDuration: { type: "number", label: "Display duration (seconds)" },
    position: {
      type: "select",
      label: "Position",
      options: [
        { label: "Bottom left", value: "bottom-left" },
        { label: "Bottom right", value: "bottom-right" },
        { label: "Top left", value: "top-left" },
        { label: "Top right", value: "top-right" },
      ],
    },
    showOnMobile: {
      type: "select",
      label: "Show on mobile",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
    maxShown: { type: "number", label: "Max shown per session" },
    randomize: {
      type: "select",
      label: "Randomize order",
      options: [
        { label: "No", value: "no" },
        { label: "Yes", value: "yes" },
      ],
    },
  },
  defaultProps: {
    notifications: [
      { name: "Ava Johnson", location: "Berlin, Germany", action: "booked a strategy call", timeAgo: "2 minutes ago" },
      { name: "Leo Martinez", location: "Madrid, Spain", action: "started a free trial", timeAgo: "9 minutes ago" },
      { name: "Mia Chen", location: "Singapore", action: "downloaded the playbook", timeAgo: "14 minutes ago" },
    ],
    displayInterval: 10,
    displayDuration: 4,
    position: "bottom-left",
    showOnMobile: "yes",
    maxShown: 3,
    randomize: "no",
  },
  render: (props: {
    notifications: Array<{ name: string; location: string; action: string; timeAgo: string }>;
    displayInterval: number;
    displayDuration: number;
    position: string;
    showOnMobile: string;
    maxShown: number;
    randomize: string;
  }) => <SocialProofPopupPreview {...props} />,
};

// ---------------------------------------------------------------------------
// 20. Survey
// ---------------------------------------------------------------------------
export const Survey = {
  fields: { question: { type: "text" }, options: { type: "textarea", label: "Options (one per line)" }, buttonLabel: { type: "text" } },
  defaultProps: { question: "What feature matters most to you?", options: "AI-powered automation\nEasy scheduling and booking\nEmail marketing\nCRM and pipeline management", buttonLabel: "Submit Answer" },
  render: ({ question, options, buttonLabel }: { question: string; options: string; buttonLabel: string }) => {
    const opts = options.split("\n").filter(Boolean);
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}` }}>
        <h3 style={{ fontSize: "1.25rem", fontWeight: 600, textAlign: "center", marginBottom: "20px", color: "var(--color-foreground)" }}>{question}</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {opts.map((opt, i) => (
            <label key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", border: "1px solid var(--color-border)", borderRadius: "8px", cursor: "pointer", background: "var(--color-card)" }}>
              <div style={{ width: "18px", height: "18px", borderRadius: "50%", border: "2px solid var(--color-border)", flexShrink: 0 }} />
              <span style={{ fontSize: "0.875rem", color: "var(--color-foreground)" }}>{opt}</span>
            </label>
          ))}
        </div>
        <button style={{ ...btnPrimary, width: "100%", marginTop: "16px" }}>{buttonLabel}</button>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 20. AppointmentForm
// ---------------------------------------------------------------------------
export const AppointmentForm = {
  fields: {
    heading: { type: "text" },
    services: { type: "textarea", label: "Services (one per line)" },
    submitLabel: { type: "text" },
  },
  defaultProps: { heading: "Book Your Appointment", services: "Initial Consultation\nFollow-up Visit\nPremium Treatment\nGroup Session", submitLabel: "Book Now" },
  render: ({ heading, services, submitLabel }: { heading: string; services: string; submitLabel: string }) => {
    const serviceList = services.split("\n").filter(Boolean);
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}` }}>
        <h3 style={{ fontSize: "1.5rem", fontWeight: 600, textAlign: "center", marginBottom: "24px", color: "var(--color-foreground)" }}>{heading}</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <input type="text" placeholder="Full name" style={inputStyle} />
          <input type="email" placeholder="Email address" style={inputStyle} />
          <select style={{ ...inputStyle, appearance: "auto" }}>
            <option value="">Select a service</option>
            {serviceList.map((s, i) => <option key={i} value={s}>{s}</option>)}
          </select>
          <input type="date" style={inputStyle} />
          <button style={{ ...btnPrimary, width: "100%" }}>{submitLabel}</button>
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 21. Newsletter
// ---------------------------------------------------------------------------
export const Newsletter = {
  fields: { heading: { type: "text" }, description: { type: "text" }, buttonLabel: { type: "text" }, placeholder: { type: "text" } },
  defaultProps: { heading: "Stay in the Loop", description: "Get weekly tips on growing your business. No spam, unsubscribe anytime.", buttonLabel: "Subscribe", placeholder: "Enter your email" },
  render: ({ heading, description, buttonLabel, placeholder }: { heading: string; description: string; buttonLabel: string; placeholder: string }) => (
    <div style={{ textAlign: "center", padding: `${spacing(6)} ${spacing(3)}`, background: "var(--color-card)", borderRadius: "12px", maxWidth: CONTENT_WIDTHS.sm, margin: "0 auto" }}>
      <h3 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "8px", color: "var(--color-foreground)" }}>{heading}</h3>
      <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", marginBottom: "20px" }}>{description}</p>
      <div style={{ display: "flex", gap: "8px", maxWidth: "400px", margin: "0 auto" }}>
        <input type="email" placeholder={placeholder} style={{ ...inputStyle, flex: 1 }} />
        <button style={btnPrimary}>{buttonLabel}</button>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 22. FileUpload
// ---------------------------------------------------------------------------
export const FileUpload = {
  fields: { label: { type: "text" }, acceptedTypes: { type: "text" }, maxSize: { type: "text" } },
  defaultProps: { label: "Upload your documents", acceptedTypes: "PDF, JPG, PNG", maxSize: "10MB" },
  render: ({ label, acceptedTypes, maxSize }: { label: string; acceptedTypes: string; maxSize: string }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: spacing(3) }}>
      <div style={{ border: "2px dashed var(--color-border)", borderRadius: "12px", padding: "40px 24px", textAlign: "center", background: "var(--color-card)" }}>
        <div style={{ fontSize: "2rem", marginBottom: "8px" }}>{"\u2191"}</div>
        <p style={{ fontWeight: 600, color: "var(--color-foreground)", marginBottom: "4px" }}>{label}</p>
        <p style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)" }}>Drag and drop or click to browse</p>
        <p style={{ fontSize: "0.75rem", color: "var(--color-muted-foreground)", marginTop: "8px" }}>Accepted: {acceptedTypes} (max {maxSize})</p>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 23. GiftCard
// ---------------------------------------------------------------------------
export const GiftCard = {
  fields: { title: { type: "text" }, amounts: { type: "text", label: "Amounts (comma separated)" }, buttonLabel: { type: "text" } },
  defaultProps: { title: "Give the Gift of Wellness", amounts: "25, 50, 100, 200", buttonLabel: "Purchase Gift Card" },
  render: ({ title, amounts, buttonLabel }: { title: string; amounts: string; buttonLabel: string }) => {
    const values = amounts.split(",").map((a) => a.trim());
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}`, textAlign: "center" }}>
        <h3 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "20px", color: "var(--color-foreground)" }}>{title}</h3>
        <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
          {values.map((v, i) => (
            <div key={i} style={{ padding: "14px 24px", borderRadius: "8px", border: i === 1 ? "2px solid var(--color-primary)" : "1px solid var(--color-border)", background: i === 1 ? "var(--color-card)" : "var(--color-background)", cursor: "pointer", fontWeight: 600, color: i === 1 ? "var(--color-primary)" : "var(--color-foreground)" }}>
              {"\u20AC"}{v}
            </div>
          ))}
        </div>
        <input type="text" placeholder="Recipient's email" style={{ ...inputStyle, marginBottom: "12px" }} />
        <textarea placeholder="Personal message (optional)" rows={2} style={{ ...inputStyle, resize: "none", marginBottom: "12px" }} />
        <button style={{ ...btnPrimary, width: "100%" }}>{buttonLabel}</button>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 24. Coupon
// ---------------------------------------------------------------------------
export const Coupon = {
  fields: { code: { type: "text" }, description: { type: "text" }, discount: { type: "text" }, expiry: { type: "text" } },
  defaultProps: { code: "LAUNCH30", description: "Get 30% off your first 3 months", discount: "30% OFF", expiry: "Expires April 30, 2026" },
  render: ({ code, description, discount, expiry }: { code: string; description: string; discount: string; expiry: string }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: spacing(3) }}>
      <div style={{ display: "flex", borderRadius: "12px", overflow: "hidden", border: "1px solid var(--color-border)" }}>
        <div style={{ background: "var(--color-primary)", color: "white", padding: "24px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "120px" }}>
          <span style={{ fontSize: "1.5rem", fontWeight: 800 }}>{discount}</span>
        </div>
        <div style={{ padding: "16px 20px", background: "var(--color-card)", flex: 1 }}>
          <p style={{ fontWeight: 600, color: "var(--color-foreground)", marginBottom: "4px" }}>{description}</p>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
            <code style={{ padding: "6px 12px", background: "var(--color-muted)", borderRadius: "4px", fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.1em", color: "var(--color-foreground)" }}>{code}</code>
            <span style={{ fontSize: "0.75rem", color: "var(--color-primary)", cursor: "pointer", fontWeight: 600 }}>Copy</span>
          </div>
          <p style={{ fontSize: "0.75rem", color: "var(--color-muted-foreground)", marginTop: "6px" }}>{expiry}</p>
        </div>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 25. PaymentButton
// ---------------------------------------------------------------------------
export const PaymentButton = {
  fields: { label: { type: "text" }, price: { type: "text" }, url: { type: "text" }, description: { type: "text" } },
  defaultProps: { label: "Buy Now", price: "\u20AC49", url: "#", description: "One-time payment. Instant access." },
  render: ({ label, price, url, description }: { label: string; price: string; url: string; description: string }) => (
    <div style={{ textAlign: "center", padding: `${spacing(4)} ${spacing(3)}` }}>
      <a href={sanitizeHref(url)} style={{ display: "inline-flex", alignItems: "center", gap: "12px", background: "var(--color-primary)", color: "white", padding: "16px 40px", borderRadius: "10px", textDecoration: "none", fontWeight: 700, fontSize: "1.1rem", boxShadow: "0 4px 14px rgba(0,0,0,0.15)" }}>
        {label} {"\u2022"} {price}
      </a>
      <p style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)", marginTop: "10px" }}>{description}</p>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 26. WaitlistForm
// ---------------------------------------------------------------------------
export const WaitlistForm = {
  fields: { heading: { type: "text" }, description: { type: "textarea" }, buttonLabel: { type: "text" }, currentCount: { type: "number" } },
  defaultProps: { heading: "Join the Waitlist", description: "Be the first to get access when we launch in your area.", buttonLabel: "Join Waitlist", currentCount: 1247 },
  render: ({ heading, description, buttonLabel, currentCount }: { heading: string; description: string; buttonLabel: string; currentCount: number }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: `${spacing(6)} ${spacing(3)}`, textAlign: "center" }}>
      <h3 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "8px", color: "var(--color-foreground)" }}>{heading}</h3>
      <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", marginBottom: "20px" }}>{description}</p>
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <input type="email" placeholder="Your email address" style={{ ...inputStyle, flex: 1 }} />
        <button style={btnPrimary}>{buttonLabel}</button>
      </div>
      <p style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)" }}>
        <span style={{ fontWeight: 700, color: "var(--color-primary)" }}>{currentCount.toLocaleString()}</span> people already on the list
      </p>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 27. CallBooking
// ---------------------------------------------------------------------------
export const CallBooking = {
  fields: { heading: { type: "text" }, description: { type: "text" }, duration: { type: "text" }, buttonLabel: { type: "text" }, phone: { type: "text" } },
  defaultProps: { heading: "Schedule a Call", description: "Talk to our team about how we can help grow your business.", duration: "15 min", buttonLabel: "Pick a Time", phone: "+1 (555) 123-4567" },
  render: ({ heading, description, duration, buttonLabel, phone }: { heading: string; description: string; duration: string; buttonLabel: string; phone: string }) => (
    <div style={{ maxWidth: CONTENT_WIDTHS.xs, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}` }}>
      <div style={{ background: "var(--color-card)", borderRadius: "12px", padding: "32px", border: "1px solid var(--color-border)", textAlign: "center" }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "var(--color-primary)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: "1.5rem" }}>{"\u260E"}</div>
        <h3 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "8px", color: "var(--color-foreground)" }}>{heading}</h3>
        <p style={{ fontSize: "0.875rem", color: "var(--color-muted-foreground)", marginBottom: "12px" }}>{description}</p>
        <div style={{ display: "inline-block", padding: "4px 12px", borderRadius: "20px", background: "var(--color-muted)", fontSize: "0.8rem", color: "var(--color-muted-foreground)", marginBottom: "16px" }}>{duration} call</div>
        <div><button style={{ ...btnPrimary, width: "100%" }}>{buttonLabel}</button></div>
        <p style={{ fontSize: "0.8rem", color: "var(--color-muted-foreground)", marginTop: "12px" }}>Or call directly: <span style={{ fontWeight: 600, color: "var(--color-foreground)" }}>{phone}</span></p>
      </div>
    </div>
  ),
};

// ---------------------------------------------------------------------------
// 28. MembershipPlans
// ---------------------------------------------------------------------------
export const MembershipPlans = {
  fields: { plans: { type: "textarea", label: "Plans (Name | Price | Features pipe-separated per line)" } },
  defaultProps: { plans: "Basic | \u20AC19/mo | 2 visits/month, Email support, Basic booking\nPremium | \u20AC49/mo | Unlimited visits, Priority support, All services, Partner discounts\nVIP | \u20AC99/mo | Unlimited visits, 24/7 concierge, Exclusive events, Personal advisor" },
  render: ({ plans }: { plans: string }) => {
    const items = plans.split("\n").filter(Boolean).map((line) => {
      const parts = line.split("|").map((s) => s.trim());
      return { name: parts[0] ?? "", price: parts[1] ?? "", features: (parts[2] ?? "").split(",").map((f) => f.trim()) };
    });
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(items.length, 3)}, 1fr)`, gap: spacing(2), padding: `${spacing(5)} ${spacing(3)}`, maxWidth: CONTENT_WIDTHS.xl, margin: "0 auto" }}>
        {items.map((plan, i) => {
          const isMiddle = i === 1;
          return (
            <div key={i} style={{ padding: "28px", borderRadius: "12px", border: isMiddle ? "2px solid var(--color-primary)" : "1px solid var(--color-border)", background: "var(--color-card)", textAlign: "center" }}>
              <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-foreground)" }}>{plan.name}</h4>
              <p style={{ fontSize: "2rem", fontWeight: 800, color: "var(--color-primary)", margin: "8px 0" }}>{plan.price}</p>
              <ul style={{ listStyle: "none", padding: 0, margin: "16px 0", textAlign: "left" }}>
                {plan.features.map((f, j) => (
                  <li key={j} style={{ padding: "6px 0", fontSize: "0.85rem", color: "var(--color-muted-foreground)", borderBottom: "1px solid var(--color-border)" }}>{"\u2713"} {f}</li>
                ))}
              </ul>
              <button style={{ ...btnPrimary, width: "100%", background: isMiddle ? "var(--color-primary)" : "var(--color-muted)", color: isMiddle ? "white" : "var(--color-foreground)" }}>Choose {plan.name}</button>
            </div>
          );
        })}
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 29. LanguageSwitcher
// ---------------------------------------------------------------------------
export const LanguageSwitcher = {
  fields: { languages: { type: "text", label: "Languages (comma separated, Code:Label)" }, defaultLanguage: { type: "text" } },
  defaultProps: { languages: "EN:English, RO:Romanian, DE:German, FR:French, ES:Spanish", defaultLanguage: "EN" },
  render: ({ languages, defaultLanguage }: { languages: string; defaultLanguage: string }) => {
    const langs = languages.split(",").map((l) => {
      const [code, label] = l.trim().split(":");
      return { code: code ?? "", label: label ?? code ?? "" };
    });
    return (
      <div style={{ display: "inline-block", padding: "8px" }}>
        <select style={{ ...inputStyle, width: "auto", padding: "8px 12px", fontSize: "0.85rem" }} defaultValue={defaultLanguage}>
          {langs.map((lang, i) => <option key={i} value={lang.code}>{lang.label}</option>)}
        </select>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 30. ClassSchedule
// ---------------------------------------------------------------------------
export const ClassSchedule = {
  fields: { classes: { type: "textarea", label: "Classes (Day | Time | Name | Instructor per line)" } },
  defaultProps: { classes: "Monday | 09:00 | Yoga Flow | Sarah\nMonday | 18:00 | HIIT Blast | Mike\nTuesday | 10:00 | Pilates | Anna\nWednesday | 09:00 | Yoga Flow | Sarah\nThursday | 18:00 | Spin Class | Jake\nFriday | 10:00 | Stretch & Recover | Anna\nSaturday | 09:00 | Weekend Warrior | Mike" },
  render: ({ classes }: { classes: string }) => {
    const items = classes.split("\n").filter(Boolean).map((line) => {
      const [day, time, name, instructor] = line.split("|").map((s) => s.trim());
      return { day: day ?? "", time: time ?? "", name: name ?? "", instructor: instructor ?? "" };
    });
    const days = [...new Set(items.map((i) => i.day))];
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.lg, margin: "0 auto", padding: spacing(3), overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: "8px", minWidth: `${days.length * 130}px` }}>
          {days.map((day) => (
            <div key={day}>
              <div style={{ padding: "8px", textAlign: "center", fontWeight: 700, fontSize: "0.85rem", color: "var(--color-foreground)", borderBottom: "2px solid var(--color-primary)" }}>{day}</div>
              {items.filter((i) => i.day === day).map((cls, j) => (
                <div key={j} style={{ marginTop: "8px", padding: "10px", background: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--color-primary)" }}>{cls.time}</div>
                  <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-foreground)", marginTop: "2px" }}>{cls.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--color-muted-foreground)" }}>{cls.instructor}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  },
};

// ---------------------------------------------------------------------------
// 31. StaffSelector
// ---------------------------------------------------------------------------
export const StaffSelector = {
  fields: { staff: { type: "textarea", label: "Staff (Name | Role | Specialty per line)" }, heading: { type: "text" }, bookLabel: { type: "text" } },
  defaultProps: { heading: "Choose Your Professional", bookLabel: "Book Now", staff: "Dr. Sarah Chen | Lead Dentist | Cosmetic & Implants\nMike Torres | Senior Therapist | Sports Recovery\nAnna Kowalski | Stylist | Color & Cuts\nJake Williams | Trainer | Strength & HIIT" },
  render: ({ staff, heading, bookLabel }: { staff: string; heading: string; bookLabel: string }) => {
    const members = staff.split("\n").filter(Boolean).map((line) => {
      const [name, role, specialty] = line.split("|").map((s) => s.trim());
      return { name: name ?? "", role: role ?? "", specialty: specialty ?? "" };
    });
    return (
      <div style={{ maxWidth: CONTENT_WIDTHS.xl, margin: "0 auto", padding: `${spacing(5)} ${spacing(3)}` }}>
        <h3 style={{ fontSize: "1.5rem", fontWeight: 700, textAlign: "center", marginBottom: "24px", color: "var(--color-foreground)" }}>{heading}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px" }}>
          {members.map((m, i) => (
            <div key={i} style={{ background: "var(--color-card)", borderRadius: "12px", padding: "20px", border: "1px solid var(--color-border)", textAlign: "center" }}>
              <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "var(--color-muted)", margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", color: "var(--color-muted-foreground)" }}>
                {m.name.charAt(0)}
              </div>
              <h4 style={{ fontWeight: 600, fontSize: "0.95rem", color: "var(--color-foreground)", margin: 0 }}>{m.name}</h4>
              <p style={{ fontSize: "0.8rem", color: "var(--color-primary)", margin: "4px 0" }}>{m.role}</p>
              <p style={{ fontSize: "0.75rem", color: "var(--color-muted-foreground)", marginBottom: "12px" }}>{m.specialty}</p>
              <button style={{ ...btnPrimary, padding: "8px 16px", fontSize: "0.8rem", width: "100%" }}>{bookLabel}</button>
            </div>
          ))}
        </div>
      </div>
    );
  },
};
